import React, { useContext, useCallback, useEffect, useMemo } from 'react';
import { Grid, CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { AlertStatus } from '@moderepo/mode-apis';
import { BaseCompAction, capitalizeAll, SlideOutComp } from '@moderepo/mode-app-components';
import { fetchRobotById, rcDataContext, selectRobotById, selectRobotCatalog } from '@moderepo/mode-data-state-robot-cloud';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import * as RCUtils from './RCUtils';
import { RCAlertInfo } from '../../components/robot-cloud';
import { RCMonitorPreviewCompOptions, RCSlideOutPanelCompType } from './RCProjectConfig';


interface RCAlertPreviewContainerProps {
    readonly projectId: number;
    readonly homeId: number;
    readonly robotId: string;
    readonly alertId: number;
    // eslint-disable-next-line react/no-unused-prop-types
    readonly context?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }
}


/**
 * This container ise used for displaying a Device's Alert status
 */
export const RCAlertPreviewContainer = (props: RCAlertPreviewContainerProps) => {
    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { projectId } = props;
    const project = selectProjectById(dataState, projectId);

    const selectedRobot = selectRobotById(rcDataState, props.homeId, props.robotId);
    const selectedAlert = selectedRobot?.status ? selectedRobot.status.alertStatuses.find((alertStatus: AlertStatus) => {
        return alertStatus.id === props.alertId;
    }) : undefined;
    const robotCatalog = selectRobotCatalog(rcDataState, projectId);
    const { trans } = useRitzTranslation(project);


    /**
     * This useEffect is used for fetching Robot data on component load
     */
    useEffect(() => {
        if (!selectedRobot) {
            rcDataDispatch(fetchRobotById(props.projectId, props.homeId, props.robotId)).catch(() => {
                // ignore error because dataDispatch already handled it
            });
        }
    }, [props.homeId, props.robotId, selectedRobot, props.projectId, rcDataDispatch]);


    /**
     * Handler for onViewMonitorDetailsClicked event. Take the user to the Monitor that the selected alert is created from
     */
    const onViewMonitorDetailsClicked = useCallback(() => {
        if (selectedAlert && selectedAlert.alertConditionId) {
            const options: RCMonitorPreviewCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : RCSlideOutPanelCompType.MONITOR_PREVIEW,
                compProps: {
                    data: {
                        projectId,
                        monitorId: selectedAlert.alertConditionId,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [projectId, selectedAlert, uiDispatch]);


    const primaryActions = useMemo(() => {
        if (selectedRobot && selectedAlert) {
            const actions: BaseCompAction[] = [{
                label  : capitalizeAll(trans('label.view_monitor_details')),
                onClick: onViewMonitorDetailsClicked,
            }];
            return actions;
        }

        return undefined;
    }, [onViewMonitorDetailsClicked, selectedAlert, selectedRobot, trans]);



    const slideOutCompContent = useMemo(() => {
        if (selectedRobot && selectedAlert) {
            return (
                <Grid container spacing={0}>
                    <Grid item xs={12}>
                        <RCAlertInfo
                            className={clsx('transparent')}
                            formProps={{
                                className: 'single-column',
                            }}
                            title={capitalizeAll(trans('label.alert_details'))}
                            icon="info"
                            fields={{
                                id: {
                                    label       : capitalizeAll(trans('label.id')),
                                    displayValue: selectedAlert.id.toString(),
                                },
                                name: {
                                    label       : capitalizeAll(trans('label.name')),
                                    displayValue: selectedAlert.name ? selectedAlert.name : capitalizeAll(trans('label.none')),
                                },
                                description: {
                                    label       : capitalizeAll(trans('label.description')),
                                    displayValue: selectedAlert.description
                                        ? selectedAlert.description : capitalizeAll(trans('label.none')),
                                },
                                monitorId: {
                                    label       : capitalizeAll(trans('label.monitor_id')),
                                    displayValue: selectedAlert.alertConditionId.toString(),
                                },
                                severity: {
                                    label       : capitalizeAll(trans('label.severity')),
                                    displayValue: selectedAlert.severity
                                        ? capitalizeAll(trans(RCUtils.getMonitorSeverityName(selectedAlert.severity)))
                                        : capitalizeAll(trans('label.none')),
                                    value: selectedAlert.severity,
                                },
                                createdConditions: {
                                    label       : capitalizeAll(trans('label.condition', { count: 2 })),
                                    displayValue: RCUtils.getMonitorConditionAsString(
                                        robotCatalog, selectedAlert.createdCondition, selectedRobot.robotClass, trans, true,
                                    ),
                                },
                                creationTime: {
                                    label       : capitalizeAll(trans('label.since')),
                                    displayValue: getDateTimeAndElapsedTime(selectedAlert.createdAt, trans),
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            );
        }

        return (
            <CircularProgress />
        );

    }, [robotCatalog, selectedAlert, selectedRobot, trans]);


    return (
        <SlideOutComp
            primaryActions={primaryActions}
            compContent={slideOutCompContent}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
